import { Injectable } from '@angular/core';
import { ContentLanguagesService } from '../utilities/fi-services/content-languages.service';
import { DropdownListSettingsLanguageModel } from '@fi-shared/dropdown';
import { TemplateLayoutModel } from './template-layout.model';
import { BaseItemModelService } from './base-item.model';
import { DocumentModel } from '../document/document.model';
import { CcmSessionData } from '@fi-app/components/core-base';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { IDropdownSelectLanguageSettingsItem } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { isDefined } from '@fi-shared/utils';

@Injectable()
export class TemplateLayoutLanguageService {
    LanguageListData: DropdownListSettingsLanguageModel<IDropdownSelectLanguageSettingsItem>;
    selectedLanguage: number;

    constructor(
        private contentLanguagesService: ContentLanguagesService,
        private userDataService: FiUserDataService,
    ) {
        this.LanguageListData = this.contentLanguagesService.GetDropdownListSettingsLanguage();

        const defaultLanguageId = this.userDataService.getUserData()?.PreferredContentLanguage;

        this.selectedLanguage = isDefined(defaultLanguageId) ? defaultLanguageId : undefined;
    }

    selectLanguage(userSelectedLanguageId: number) {
        this.selectedLanguage = userSelectedLanguageId;
    }

    IsLayoutLanguageSelected(contentLanguageId: number) {
        return this.selectedLanguage == contentLanguageId;
    }

    IsDefaultLanguage(contentLanguageId: number) {
        return contentLanguageId === this.GetDefaultLanguageId();
    }

    GetDefaultLanguageId() {
        const languageId = this.userDataService.getUserData()?.PreferredContentLanguage;
        return isDefined(languageId) ? languageId : null;
    }

    iterateTemplateLayoutItems(
        templateLayout: TemplateLayoutModel,
        callback: (item: BaseItemModelService) => void | boolean,
    ) {
        const result: BaseItemModelService[] = [];
        templateLayout.placeholders.forEach((placeholder) => {
            if (placeholder.children) {
                placeholder.children.forEach((childItem) => {
                    childItem.items.forEach((item) => {
                        if (callback(item) !== false) {
                            result.push(item);
                        }
                    });
                });
            } else {
                placeholder.items.forEach((item) => {
                    if (callback(item) !== false) {
                        result.push(item);
                    }
                });
            }
        });
        return result;
    }

    GetLayoutLanguages(templateLayout: TemplateLayoutModel): number[] {
        const result: number[] = [];
        this.iterateTemplateLayoutItems(templateLayout, (item) => {
            item.model.Contents.forEach((content) => {
                if (
                    !item.IsEmpty(content.ContentLanguageId) &&
                    content.DocumentControlContent && // https://chainformation.atlassian.net/browse/FI-3003
                    result.indexOf(content.ContentLanguageId) === -1
                ) {
                    result.push(content.ContentLanguageId);
                }
            });
        });

        return result;
    }

    InitDefaultLanguageFromLayout(templateLayout: TemplateLayoutModel): boolean {
        const languages = this.GetLayoutLanguages(templateLayout);
        return this.InitDefaultLanguage(languages);
    }

    InitDefaultLanguage(languages: number[]): boolean {
        if (languages.indexOf(this.selectedLanguage) > -1) {
            return true;
        }

        if (languages.length === 0 || languages.indexOf(this.userDataService.userData.PreferredContentLanguage) > -1) {
            this.selectedLanguage = this.userDataService.userData.PreferredContentLanguage;
            return true;
        } else if (languages.indexOf(this.GetDefaultLanguageId()) > -1) {
            this.selectedLanguage = this.GetDefaultLanguageId();
            return true;
        } else if (languages.indexOf(this.selectedLanguage) == -1) {
            this.selectedLanguage = languages[0];
            return false;
        } else {
            return true;
        }
    }
}
